import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { appraisalStatusLabel, getTeamAppraisalCycles, getTeamAppraisals } from "./appraisalApi";
import { AppraisalEmployeeCell } from "./AppraisalEmployeeCell";
import AppraisalAssessmentPage from "./AppraisalAssessmentPage";
import "./Appraisal.css";

const normalizeCycle = (c) => ({
  id: c.id ?? c.Id,
  appraisalDate: c.appraisalDate ?? c.AppraisalDate,
  isAllOffices: Boolean(c.isAllOffices ?? c.IsAllOffices),
  officeId: c.officeId ?? c.OfficeId ?? null,
  officeName: c.officeName ?? c.OfficeName ?? "",
  label: c.label ?? c.Label ?? "",
  isLatest: Boolean(c.isLatest ?? c.IsLatest),
  teamCount: Number(c.teamCount ?? c.TeamCount ?? 0),
});

const normalizeRow = (r) => ({
  id: r.id ?? r.Id,
  userId: r.userId ?? r.UserId,
  userName: r.userName ?? r.UserName ?? "",
  designation: r.designation ?? r.Designation ?? "",
  department: r.department ?? r.Department ?? "",
  officeName: r.officeName ?? r.OfficeName ?? "",
  profileImageUrl: String(r.profileImageUrl ?? r.ProfileImageUrl ?? "").trim() || null,
  appraisalDate: r.appraisalDate ?? r.AppraisalDate,
  status: r.status ?? r.Status,
  companyAppraisalId: r.companyAppraisalId ?? r.CompanyAppraisalId,
  cycleLabel: r.cycleLabel ?? r.CycleLabel ?? "",
  isLatestCycle: Boolean(r.isLatestCycle ?? r.IsLatestCycle),
});

const openActionLabel = (status) => {
  switch (Number(status)) {
    case 0:
      return "View";
    case 1:
      return "Assess";
    case 2:
      return "Confirm meeting";
    case 3:
      return "View";
    default:
      return "Open";
  }
};

const AppraisalTeamPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState("");
  const [selectedAppraisalId, setSelectedAppraisalId] = useState(null);

  const selectedCycle = useMemo(
    () => cycles.find((c) => String(c.id) === String(selectedCycleId)) || null,
    [cycles, selectedCycleId]
  );

  const loadCycles = useCallback(async () => {
    const data = await getTeamAppraisalCycles();
    const list = (Array.isArray(data) ? data : []).map(normalizeCycle);
    setCycles(list);
    setSelectedCycleId((prev) => {
      if (prev && list.some((c) => String(c.id) === String(prev))) return prev;
      const latest = list.find((c) => c.isLatest) || list[0];
      return latest?.id ? String(latest.id) : "";
    });
    return list;
  }, []);

  const loadRows = useCallback(async (cycleId) => {
    setLoading(true);
    setError("");
    try {
      const data = await getTeamAppraisals(cycleId || undefined);
      setRows((Array.isArray(data) ? data : []).map(normalizeRow));
    } catch (e) {
      setError(e?.message || "Failed to load team appraisals");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        await loadCycles();
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Failed to load appraisal cycles");
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadCycles]);

  useEffect(() => {
    if (!selectedCycleId) {
      setLoading(false);
      setRows([]);
      return;
    }
    loadRows(selectedCycleId);
  }, [selectedCycleId, loadRows]);

  const openAppraisal = (row) => {
    setSelectedAppraisalId(row.id);
  };

  if (selectedAppraisalId) {
    return (
      <AppraisalAssessmentPage
        mode="team"
        embeddedAppraisalId={selectedAppraisalId}
        onClose={() => setSelectedAppraisalId(null)}
      />
    );
  }

  return (
    <div className="appraisal-page appraisal-team-page">
      <CommonPageHeader
        icon={<i className="bi bi-people" />}
        title={t("app.appraisal.teamTitle", { defaultValue: "Team Appraisals" })}
        description={t("app.appraisal.teamSubtitle", {
          defaultValue:
            "Review and assess your direct reports. Pick a cycle to see who belongs to that round.",
        })}
      />

      {cycles.length > 0 ? (
        <div className="appraisal-team-cycle-bar">
          <label htmlFor="appraisal-team-cycle">
            {t("app.appraisal.cycleFilter", { defaultValue: "Appraisal cycle" })}
          </label>
          <select
            id="appraisal-team-cycle"
            value={selectedCycleId}
            onChange={(e) => setSelectedCycleId(e.target.value)}
          >
            {cycles.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
                {c.isLatest
                  ? t("app.appraisal.cycleLatestSuffix", { defaultValue: " (latest)" })
                  : ""}
                {c.teamCount > 0 ? ` · ${c.teamCount}` : ""}
              </option>
            ))}
          </select>
          {selectedCycle ? (
            <span className="appraisal-team-cycle-bar__hint">
              {selectedCycle.isLatest
                ? t("app.appraisal.viewingLatestCycle", {
                    defaultValue: "Viewing the latest cycle",
                  })
                : t("app.appraisal.viewingPastCycle", {
                    defaultValue: "Viewing a previous cycle",
                  })}
            </span>
          ) : null}
        </div>
      ) : null}

      {loading ? <div className="appraisal-muted">Loading…</div> : null}
      {error ? <div className="appraisal-error">{error}</div> : null}
      {!loading && rows.length === 0 ? (
        <div className="appraisal-team-empty">
          <i className="bi bi-people" aria-hidden />
          <p>
            {t("app.appraisal.noTeamItemsForCycle", {
              defaultValue: "No team appraisals for this cycle.",
            })}
          </p>
        </div>
      ) : null}

      {!loading && rows.length > 0 ? (
        <div className="appraisal-table-wrap appraisal-team-table-wrap">
          <table className="appraisal-table appraisal-team-table">
            <thead>
              <tr>
                <th className="appraisal-team-col-employee">
                  {t("app.appraisal.colEmployee", { defaultValue: "Employee" })}
                </th>
                <th>{t("app.appraisal.colCycle", { defaultValue: "Cycle" })}</th>
                <th>{t("app.appraisal.colDate", { defaultValue: "Date" })}</th>
                <th>{t("app.appraisal.colStatus", { defaultValue: "Status" })}</th>
                <th className="appraisal-team-col-actions">
                  {t("app.appraisal.colActions", { defaultValue: "Actions" })}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="appraisal-team-col-employee">
                      <AppraisalEmployeeCell
                        name={r.userName}
                        designation={r.designation}
                        department={r.department}
                        officeName={r.officeName}
                        profileImageUrl={r.profileImageUrl}
                      />
                    </td>
                    <td>
                      <span className="appraisal-cycle-badge" title={r.cycleLabel}>
                        <span className="appraisal-cycle-badge__label">
                          {r.cycleLabel || "—"}
                        </span>
                        {r.isLatestCycle ? (
                          <span className="appraisal-cycle-badge__latest">
                            {t("app.appraisal.latestShort", { defaultValue: "Latest" })}
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td className="appraisal-team-col-date">
                      {r.appraisalDate
                        ? new Date(r.appraisalDate).toLocaleDateString()
                        : "—"}
                    </td>
                    <td>
                      <span
                        className={`appraisal-status-pill appraisal-status-pill--${Number(r.status) || 0}`}
                      >
                        {appraisalStatusLabel(r.status)}
                      </span>
                    </td>
                    <td className="appraisal-team-col-actions">
                      <button
                        type="button"
                        className="appraisal-btn primary appraisal-team-action"
                        onClick={() => openAppraisal(r)}
                      >
                        <i className="bi bi-box-arrow-up-right" aria-hidden />
                        {t(`app.appraisal.teamAction.${Number(r.status)}`, {
                          defaultValue: openActionLabel(r.status),
                        })}
                      </button>
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

export default AppraisalTeamPage;
